import { PlusCircle } from "lucide-react";
import { type Dispatch, type SetStateAction } from "react";
import { TABS } from "./Tabs";
import ProgramCard from "../programs/ProgramCard";
import type { ProgramInterface } from "../../types/program.interface";
import CommentList from "./CommentsList";
import type { Comment } from "./CommentsList";
import { StartProgramButton } from "./StartProgramButton";
import type { UserProgram } from "../../types/userProgram.interface";
import { ProgramProgress } from "./ProgramProgress";

const intensityLabel = (intensity: number) => {
  switch (intensity) {
    case 0:
      return "Athlete";
    case 1:
      return "High";
    case 2:
      return "Medium";
    case 3:
      return "Low";
    default:
      return "";
  }
};

interface Props {
  program: ProgramInterface
  otherPrograms: ProgramInterface[]
  userProgram?: UserProgram
  setActiveTab: Dispatch<SetStateAction<string>>
  setOpenEnroll: () => void
  reviews: Comment[]
}

export const Start = ({ program, setActiveTab, userProgram, setOpenEnroll, reviews, otherPrograms }: Props) => {
  return (
    <>
      <div className="font-juana text-3xl mb-5 mt-20">Sobre el programa</div>
      <div className="font-md pb-5">{program.description}</div>

      <div className="flex flex-wrap justify-around items-center border border-gray-200 bg-white mt-10 rounded-md shadow-md">
        <div className="flex-grow border-r border-gray-200 p-4">
          <div className="mb-2">
            <img
              src="https://cache.beholos.com/img/fitness-pass-v2/global/calendar-icon.svg"
              className="h-7"
              alt="calendar"
            />
          </div>
          <h2 className="font-light mb-2">{program.days} días</h2>
          <div className="font-bold uppercase text-gray-500 text-xs">
            duración total
          </div>
        </div>
        <div className="flex-grow border-r border-gray-200 p-4">
          <div className="mb-2">
            <img
              src="https://cache.beholos.com/img/fitness-pass-v2/global/clock-icon.svg"
              className="h-7"
              alt="clock"
            />
          </div>
          <h2 className="font-light mb-2">{program.classLength}</h2>
          <div className="font-bold uppercase text-gray-500 text-xs">Por día</div>
        </div>
        <div className="flex-grow border-r border-gray-200 p-4">
          <div className="mb-2">
            <img
              src="https://cache.beholos.com/img/fitness-pass-v2/global/dumbell-icon.svg"
              className="h-7"
              alt="dumbbell"
            />
          </div>
          <h2 className="font-light mb-2">{program?.categories?.[0]?.name ?? ''}</h2>
          <div className="font-bold uppercase text-gray-500 text-xs">
            Tipo de actividad
          </div>
        </div>
        <div className="flex-grow p-4">
          <div className="mb-2">
            <img
              src="https://cache.beholos.com/img/fitness-pass-v2/global/flame-icon.svg"
              className="h-7"
              alt="flame"
            />
          </div>
          <h2 className="font-light mb-2">{intensityLabel(program.intensity ?? 0)}</h2>
          <div className="font-bold text-gray-500 text-xs">Nivel</div>
        </div>
      </div>

      {userProgram ? <ProgramProgress
        program={program}
        userProgram={userProgram}
        total={program.weeks?.reduce((acc, week) => acc + week.length, 0) ?? 0}
        showSignUpModal={setOpenEnroll}
        goToVideos={() => setActiveTab(TABS[1])}
      /> : <StartProgramButton setOpenEnroll={setOpenEnroll} />}

      <div className="text-left pt-5 border-t border-gray-300 pt-10 mt-15">
        <h2 className="mb-3 text-3xl font-light font-juana">Plan del programa</h2>
        <div className="mb-4 text-md text-gray-700">
          Este programa incluye contenido exclusivo, un calendario y material descargable para ayudarte a cumplir tus
          objetivos. Aquellos que invitan amigos a realizar nuestros programas, tienen un 30% más de probabilidad a
          completarlo y tener éxito. ¡Compártelo! Si tienes alguna duda, escríbenos a{" "}
          <a href="mailto:contacto@beholos.com" className="text-holos-dark underline">
            contacto@beholos.com
          </a>
          .
        </div>

        <div className="flex justify-around my-5 flex-wrap bg-white rounded-md shadow-md py-6">
          <div className="mb-4">
            <div>
              <a onClick={() => setActiveTab(TABS[1])}>
                <img
                  src="https://cache.beholos.com/img/fitness-pass-v2/life/personal-training/sessions-v2.png"
                  alt="Contenido exclusivo"
                  className="mx-auto"
                  style={{ height: 150 }}
                />
              </a>
            </div>
            <h6 className="mt-4 text-center font-bold">
              <a onClick={() => setActiveTab(TABS[1])} className="text-black cursor-pointer">
                Contenido exclusivo
              </a>
            </h6>
          </div>

          <div className="hidden md:flex items-center justify-center mb-4">
            <PlusCircle className="text-black" size={24} />
          </div>

          <div className="mb-4">
            <div>
              <a onClick={() => setActiveTab(TABS[2])}>
                <img
                  src="https://cache.beholos.com/img/fitness-pass-v2/life/personal-training/final-1-on-1-v2-.png"
                  alt="Cronograma"
                  className="mx-auto"
                  style={{ height: 150 }}
                />
              </a>
            </div>
            <h6 className="mt-4 text-center font-bold">
              <a onClick={() => setActiveTab(TABS[1])} className="text-black cursor-pointer">
                Cronograma
              </a>
            </h6>
          </div>

          <div className="hidden md:flex items-center justify-center mb-4">
            <PlusCircle className="text-black" size={24} />
          </div>

          <div className="mb-4 flex items-center">
            <a onClick={() => setActiveTab(TABS[1])} className="w-full text-center cursor-pointer">
              <div style={{ height: 150 }} className="flex flex-col justify-center">
                <img
                  src="https://cache.beholos.com/img/fitness-pass-v2/life/personal-training/cronograma002.png"
                  alt="Material descargable"
                  className="mx-auto"
                  style={{ height: 100 }}
                />
              </div>
              <h6 className="mt-4 font-bold text-center text-black">Material descargable</h6>
            </a>
          </div>
        </div>
      </div>

      <div className="text-left mb-5 pt-15 mt-10 border-t border-gray-200">
        <h2 className="mb-3 text-3xl font-light font-juana">Equipamiento o accesorios recomendados</h2>
        <div className="mb-4 text-md text-gray-700">
          Lleva tu programa al siguiente nivel. Para tu conveniencia, tenemos artículos de diferentes proveedores en
          nuestra tienda online (delivery a todo el Perú). Muy pronto tendremos delivery en más países.
        </div>

        <div className="flex flex-wrap justify-around mb-3">
          {program.equipment?.map((equipment, index) => (
            <div key={index} className="mb-3">
              <div className="px-2">
                <div className="block mx-auto mb-2">
                  <img
                    src={`https://image.beholos.com/400x400/filters:quality(90)${equipment.image}`}
                    alt={equipment.name}
                    className="mx-auto"
                    style={{ height: 200 }}
                  />
                </div>
                <div className="block text-center capitalize text-gray-600 text-sm">
                  {equipment.name}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <CommentList comments={reviews} />

      <div className="text-left mb-5 pt-15 mt-10 border-t border-gray-200">
        <h2 className="mb-3 text-3xl font-light font-juana">Otros programas que te pueden gustar</h2>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {otherPrograms.map((program: ProgramInterface, index: number) => <ProgramCard key={index} program={program} />)}
          </div>
        </div>
      </div>
    </>
  )
}